/**
 * Тонкий слой над fetch. Ровно три обязанности:
 *   1. сессионная кука уходит с каждым запросом (credentials: include);
 *   2. CSRF-токен из /api/auth/me прикладывается заголовком к КАЖДОМУ POST (Д-В11);
 *   3. 401 отличается от прочих ошибок отдельным классом — на нём построен
 *      редирект на вход с возвратом на исходный маршрут (Д-Ф4).
 *
 * Здесь НЕТ приведений `as` на данных ответа: `json()` возвращает unknown,
 * а тип задаётся вызывающей стороной из сгенерированной схемы.
 */

type QueryPrimitive = string | number | boolean;
export type QueryParams = Record<
  string,
  QueryPrimitive | readonly QueryPrimitive[] | null | undefined
>;

export class ApiError extends Error {
  readonly status: number;
  readonly detail: unknown;

  constructor(status: number, message: string, detail: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

export class UnauthorizedError extends ApiError {
  constructor(detail: unknown) {
    super(401, 'Сессия недействительна', detail);
    this.name = 'UnauthorizedError';
  }
}

// CSRF живёт в памяти модуля. В localStorage не кладём (Д-Ф3):
// второй источник истины начинает врать при выходе в другой вкладке.
let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

/** Базовый путь приложения без завершающего слэша (Д-Ф11). */
function basePrefix(): string {
  const base = import.meta.env.BASE_URL || '/';
  return base === '/' ? '' : base.replace(/\/$/, '');
}

export function apiUrl(path: string, params?: QueryParams): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const item of value) search.append(key, String(item));
    } else {
      search.set(key, String(value));
    }
  }
  const query = search.toString();
  return `${basePrefix()}${path}${query ? `?${query}` : ''}`;
}

async function readBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function extractMessage(detail: unknown, fallback: string): string {
  // Сужение через `in` даёт доступ к полю без приведения `as` (запрещённого в
  // этом слое): TS сам выводит { detail: unknown } после проверки.
  if (detail && typeof detail === 'object' && 'detail' in detail) {
    const value = detail.detail;
    if (typeof value === 'string') return value;
  }
  return fallback;
}

async function request<T>(path: string, init: RequestInit): Promise<T> {
  const response = await fetch(path, { credentials: 'include', ...init });

  if (!response.ok) {
    const detail = await readBody(response);
    if (response.status === 401) throw new UnauthorizedError(detail);
    throw new ApiError(
      response.status,
      extractMessage(detail, `${response.status} ${response.statusText}`),
      detail,
    );
  }

  const body: unknown = await response.json();
  // Единственная точка входа данных в приложение. Тип T приходит из
  // сгенерированной схемы; проверять форму в рантайме избыточно — расхождение
  // ловится сверкой schema.d.ts в CI (Task 2). Директива стоит вплотную к строке,
  // иначе она не привязывается к нужному оператору.
  // eslint-disable-next-line no-restricted-syntax -- см. комментарий выше
  return body as T;
}

export function apiGet<T>(path: string, params?: QueryParams): Promise<T> {
  return request<T>(apiUrl(path, params), { method: 'GET' });
}

export function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return mutate<T>('POST', path, body);
}

/** T28: правка наблюдения. Тот же CSRF-контур, что и POST. */
export function apiPatch<T>(path: string, body: unknown): Promise<T> {
  return mutate<T>('PATCH', path, body);
}

/** T29: полная замена ресурса (настройки). Тот же CSRF-контур. */
export function apiPut<T>(path: string, body: unknown): Promise<T> {
  return mutate<T>('PUT', path, body);
}

/** T28: удаление наблюдения. Ответ — снимок удалённого (контракт). */
export function apiDelete<T>(path: string): Promise<T> {
  return mutate<T>('DELETE', path);
}

export async function apiPostDownload(path: string, body: unknown): Promise<Blob> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
  const response = await fetch(apiUrl(path), {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const detail = await readBody(response);
    if (response.status === 401) throw new UnauthorizedError(detail);
    throw new ApiError(
      response.status,
      extractMessage(detail, `${response.status} ${response.statusText}`),
      detail,
    );
  }
  return response.blob();
}

function mutate<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  return request<T>(apiUrl(path), {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
}
